import {
  getList,
  LeoUUID,
  getObject,
  getLeoTimestamp,
  LeoTimestamp,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { APIKey, APIKeyDTO } from "./apiKey";
/* eslint-disable import/export */
export abstract class GenerateNewAPIKeyRPC implements
  LeoRPC<GenerateNewAPIKeyRPC.Request, GenerateNewAPIKeyRPC.Response, GenerateNewAPIKeyRPC.Errors.Errors>
{
  abstract execute(
    request: GenerateNewAPIKeyRPC.Request
  ): Promise<LeoRPCResult<GenerateNewAPIKeyRPC.Response, GenerateNewAPIKeyRPC.Errors.Errors>>;
}

export namespace GenerateNewAPIKeyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectIds: string[];
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGenerateNewAPIKeyRPCRequest: boolean = true;
  
    readonly projectIds: LeoUUID[];
  
    static readonly constraints = {
      "projectIds": {
        "minSize": 1,
      }
    }
  
    constructor(
    projectIds: LeoUUID[]
    ) {
      Request.validateProjectIds(projectIds);
      this.projectIds = projectIds;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectIds = getList<string, LeoUUID>(
        dto,
        "projectIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestProjectIdsError
      );
      return new Request(
        projectIds
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectIds: this.projectIds.map((e: LeoUUID) => { return e.toDTO(); })
      };
      return dto;
    }
    private static validateProjectIds = (projectIds: LeoUUID[]): void => {
      if(projectIds.length < 1) {
        throw new InvalidRequestProjectIdsMinSizeError(
          1,
          `Attribute projectIds is too short. Size is ${projectIds.length}. Min size is 1.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GenerateNewAPIKeyRPC(projectIds='${this.projectIds}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    newAPIKey: APIKeyDTO;
    expiresAt: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGenerateNewAPIKeyResponse: boolean = true;
  
    readonly newAPIKey: APIKey;
    readonly expiresAt: LeoTimestamp;
  
  
    constructor(
    newAPIKey: APIKey, 
    expiresAt: LeoTimestamp
    ) {
      this.newAPIKey = newAPIKey;
      this.expiresAt = expiresAt;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const newAPIKey = APIKey.fromDTO(getObject(dto, "newAPIKey", InvalidResponseNewApiKeyError));
      const expiresAt = getLeoTimestamp(dto, "expiresAt", InvalidResponseExpiresAtError);
      return new Response(
        newAPIKey,
        expiresAt
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          newAPIKey: this.newAPIKey.toDTO(),
          expiresAt: this.expiresAt.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GenerateNewAPIKey(newAPIKey='${this.newAPIKey}',expiresAt='${this.expiresAt}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GenerateNewAPIKeyRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidProjectId;
    export type ErrorsDTO = InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdsError extends GenerateNewAPIKeyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdsError";
  }
}

export class InvalidRequestProjectIdsMinSizeError extends InvalidRequestProjectIdsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdsMinSizeError";
  }
}

export class InvalidResponseNewApiKeyError extends GenerateNewAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNewApiKeyError";
  }
}

export class InvalidResponseExpiresAtError extends GenerateNewAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseExpiresAtError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GenerateNewAPIKeyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

